import React, { useState, useMemo } from 'react';
import { Button, Modal, Table, Badge, Row, Col, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { NoDataAlert } from '../common';

const DuplicateMembersFinder = ({ duplicateMembers, handleDeletion }) => {
  const [showModal, setShowModal] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [search, setSearch] = useState('');
  const [minGroups, setMinGroups] = useState(2);
  const [duplicates, setDuplicates] = useState(duplicateMembers);

  const [selectedMember, setSelectedMember] = useState(null);
  const [groupsToRemove, setGroupsToRemove] = useState([]);

  const filteredDuplicates = useMemo(
    () =>
      duplicates.filter(
        (m) =>
          m.groups.length >= minGroups &&
          m.memberName.toLowerCase().includes(search.toLowerCase())
      ),
    [duplicates, search, minGroups]
  );

  const handleFindDuplicates = () => {
    setDuplicates(duplicateMembers);
    setShowModal(true);
  };

  const openManageModal = (member) => {
    setSelectedMember(member);
    setGroupsToRemove([]);
    setShowManage(true);
  };

  const toggleGroupRemoval = (groupId) => {
    setGroupsToRemove((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const confirmRemoval = () => {
    handleDeletion({
      teamId: groupsToRemove,
      selectedMember: [selectedMember.memberId],
    });
    setDuplicates((prev) =>
      prev
        .map((member) => {
          if (member.memberId !== selectedMember.memberId) return member;

          return {
            ...member,
            groups: member.groups.filter(
              (g) => !groupsToRemove.includes(g.groupId)
            ),
          };
        })
        .filter((m) => m.groups.length > 1)
    );

    setShowManage(false);
  };

  return (
    <>
      <Button
        variant="outline-primary"
        size="sm"
        className="me-2"
        onClick={handleFindDuplicates}
      >
        Find Duplicate Members
      </Button>

      {/* MAIN MODAL */}
      <Modal
        show={showModal}
        size="xl"
        backdrop="static"
        onHide={() => setShowModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="mb-0">Duplicate Members</Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{ maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' }}
        >
          {/* Filters */}
          {filteredDuplicates.length === 0 && (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Control
                  placeholder="Search member..."
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
            </Row>
          )}

          {/* Empty State */}
          {filteredDuplicates.length === 0 && (
            <NoDataAlert content="No duplicate members found!" />
          )}

          {filteredDuplicates.length > 0 && (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Groups</th>
                  <th width="120">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredDuplicates.map((member) => (
                  <tr key={member.memberId}>
                    <td>
                      <strong>{member.memberName}</strong>
                      <div className="text-muted small">
                        {member.groups.length} groups
                      </div>
                    </td>

                    <td>
                      {member.groups.map((group) => (
                        <Badge
                          key={group.groupId}
                          variant="secondary"
                          className="me-2 mb-2 bg-primary-soft"
                        >
                          {group.groupName}
                        </Badge>
                      ))}
                    </td>

                    <td>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => openManageModal(member)}
                      >
                        Manage
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MANAGE MEMBER MODAL */}
      {selectedMember && (
        <Modal
          show={showManage}
          onHide={() => setShowManage(false)}
          centered
          backdropClassName="inner-modal-backdrop"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Manage Groups – {selectedMember.memberName}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedMember.groups.map((group) => (
              <Form.Check
                key={group.groupId}
                type="checkbox"
                label={group.groupName}
                onChange={() => toggleGroupRemoval(group.groupId)}
                className="mb-2"
              />
            ))}
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="outline-primary"
              onClick={() => setShowManage(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={groupsToRemove.length === 0}
              onClick={confirmRemoval}
            >
              Remove from Selected Groups
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

DuplicateMembersFinder.propTypes = {
  duplicateMembers: PropTypes.array,
  handleDeletion: PropTypes.func,
};

export default DuplicateMembersFinder;
